"use client";

import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import NotFoundState from "@/components/ui/NotFoundState";
import { PATHS } from "@/constants/pathnames";
import { useModalHelpers } from "@/contexts/ModalContext";
import { useDebounce } from "@/hooks/useDebounce";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { Link, useRouter } from "@/i18n/navigation";
import { faqService } from "@/services/faqService";
import { getLocalizedPath } from "@/utils/pathUtils";
import { sendGTMEvent } from "@next/third-parties/google";
import { useSearchParams } from "next/navigation";
import Spinner from "./ui/Spinner";

// Highlight matching text in search results
const highlightText = (text, searchTerm) => {
  if (!searchTerm || !text) return text;

  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedSearchTerm})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, index) => {
    // Check if this part matches the search term (case-insensitive)
    if (part.toLowerCase() === searchTerm.toLowerCase()) {
      return (
        <strong key={index} className="font-bold">
          {part}
        </strong>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

export default function FaqSearch() {
  const t = useTranslationDetector();
  const router = useRouter();
  const locale = useLocale();
  const { warningModal } = useModalHelpers();

  const [inputValue, setInputValue] = useState("");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const lastQueryRef = useRef("");

  const debouncedQuery = useDebounce(inputValue.trim(), 300);
  const escapePressed = useKeyPressed("Escape");
  const searchParams = useSearchParams();

  const faqBasePath = useMemo(
    () => getLocalizedPath(PATHS.FAQ_PAGE.PATH, locale),
    [locale],
  );

  const resetSearch = useCallback((opts = { keepFocus: false }) => {
    setInputValue("");
    setResults([]);
    setIsOpen(false);
    setIsLoading(false);
    lastQueryRef.current = "";
    if (opts.keepFocus) inputRef.current?.focus();
  }, []);

  const closeList = useCallback(() => setIsOpen(false), []);

  const searchFaq = useCallback(
    async (query) => {
      const q = query?.trim();
      if (!q) {
        setResults([]);
        lastQueryRef.current = "";
        return;
      }

      // same query => no request
      if (lastQueryRef.current === q) return;

      lastQueryRef.current = q;
      setIsLoading(true);

      try {
        const response = await faqService.searchFaq(
          {
            searchTerm: q,
          },
          {
            "Accept-Language": searchParams?.get("lang")
              ? searchParams?.get("lang")
              : locale,
          },
        );
        setResults(response?.Results);
        sendGTMEvent({
          event: "custom_event",
          event_category: "complete_search",
          event_action: "complete_search",
          search_term: q,
        });
      } catch (e) {
        setResults([]);
        lastQueryRef.current = "";
      } finally {
        setIsLoading(false);
      }
    },
    [locale],
  );

  // Debounced search lifecycle
  useEffect(() => {
    if (!debouncedQuery) {
      setResults([]);
      setIsOpen(false);
      lastQueryRef.current = "";
      return;
    }

    setIsOpen(true);
    searchFaq(debouncedQuery);
  }, [debouncedQuery, searchFaq]);

  // ESC closes list
  useEffect(() => {
    if (!escapePressed || !isOpen) return;
    closeList();
    inputRef.current?.blur();
  }, [escapePressed, isOpen, closeList]);

  // Click outside closes list
  useOnClickOutside(
    containerRef,
    useCallback(() => {
      if (isOpen) closeList();
    }, [isOpen, closeList]),
  );

  const goToQuestion = useCallback(
    (item) => {
      router.push(
        `/${item?.categorySlug}/${item?.questionSlug}${searchParams?.get("lang") ? `?lang=${searchParams?.get("lang")}` : ""}`,
      );
      setIsOpen(false);
      setInputValue("");
      setResults([]);
      lastQueryRef.current = "";
    },
    [router],
  );

  const handleKeyDown = useCallback(
    (e) => {
      // allow typing when closed; only act on keys we care about
      const key = e.key;

      if (key !== "Enter") return;

      e.preventDefault();
      const trimmed = inputValue.trim();

      if (!trimmed) {
        warningModal({
          title: searchParams?.get("lang")
            ? t(`common.defaultWarningTitle${searchParams?.get("lang")}`)
            : t("common.defaultWarningTitle"),
          message: searchParams?.get("lang")
            ? t(`common.faqSearchEmptyWarning${searchParams?.get("lang")}`)
            : t("common.faqSearchEmptyWarning"),
        });
        return;
      }

      if (results.length > 0) goToQuestion(results[0]);
    },
    [inputValue, results, goToQuestion, warningModal, t],
  );

  const handleClear = useCallback(() => {
    resetSearch({ keepFocus: true });
  }, [resetSearch]);

  const showDropdown = isOpen && (isLoading || !!debouncedQuery);
  const showResults = showDropdown && !isLoading && results.length > 0;
  const showNoResults =
    showDropdown && !isLoading && debouncedQuery && results.length === 0;

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => {
            debouncedQuery && setIsOpen(true);
            sendGTMEvent({
              event: "custom_event",
              event_category: "click_search",
              event_action: "click_search",
            });
          }}
          onKeyDown={handleKeyDown}
          placeholder={
            searchParams?.get("lang")
              ? t(`common.faqSearchPlaceholder${searchParams?.get("lang")}`)
              : t("common.faqSearchPlaceholder")
          }
          className="bg-background placeholder:text-muted-foreground focus:ring-primary shadow-small w-full rounded-lg border-none p-5 font-semibold transition-all duration-200 outline-none focus:ring-2"
          aria-autocomplete="list"
          id="faq-search-input"
        />

        {isLoading ? (
          <Spinner className="text-primary absolute top-1/2 right-5 h-6 w-6 -translate-y-1/2 animate-spin" />
        ) : inputValue ? (
          <button
            type="button"
            onClick={handleClear}
            className="bg-background-400 text-foreground-muted absolute top-1/2 right-5 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full transition-all duration-200"
            aria-label={t("common.clear")}
          >
            <FontAwesomeIcon
              icon={byPrefixAndName.far["xmark"]}
              className="text-sm"
            />
          </button>
        ) : (
          <FontAwesomeIcon
            icon={byPrefixAndName.fas["magnifying-glass"]}
            className="text-foreground-muted absolute top-1/2 right-5 h-6 w-6 -translate-y-1/2"
          />
        )}
      </div>

      {showDropdown && (
        <div
          id="faq-search-list"
          className="shadow-xxl absolute top-full right-0 left-0 z-10 mt-1 max-h-[280px] overflow-y-auto rounded-lg bg-white"
        >
          {showResults && (
            <ul>
              {results.map((item, index) => {
                return (
                  <li key={index}>
                    <Link
                      href={`${faqBasePath}/${item?.FaqSlug}${searchParams?.get("lang") ? `?lang=${searchParams?.get("lang")}` : ""}`}
                      className="hover:bg-background-global block cursor-pointer px-6 py-5 transition-colors"
                      onClick={() => {
                        sendGTMEvent({
                          event: "custom_event",
                          event_category: "click_search_term",
                          event_action: "click_search_term",
                          search_term: item?.Question,
                          search_category: "related_results",
                        });
                      }}
                    >
                      {highlightText(item?.Question, debouncedQuery)}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}

          {showNoResults && (
            <NotFoundState
              title={
                searchParams?.get("lang")
                  ? t(`common.noResults${searchParams?.get("lang")}`)
                  : t("common.noResults")
              }
              description={
                searchParams?.get("lang")
                  ? t(`common.noResultsDescription${searchParams?.get("lang")}`)
                  : t("common.noResultsDescription")
              }
            />
          )}
        </div>
      )}
    </div>
  );
}
